import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Table, Dropdown, theme } from 'antd';
import {
  PlusOutlined,
  MoreOutlined,
  CheckOutlined,
  EditOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { VendorLocationResponse, VendorLocationSortField } from '@/types/api';
import TableContainer from '@/components/common/TableContainer';
import { LocationAddressDisplay } from '@/components/common/LocationAddressDisplay';
import { getToggleStatusStyle } from '@/hooks/useVendorPermissions';
import { controlHeightTokens } from '@/styles/tokens';

interface LocationsSectionProps {
  locations: VendorLocationResponse[];
  loading: boolean;
  includeInactive: boolean;
  onIncludeInactiveChange: (checked: boolean) => void;
  canEdit: boolean;
  canAdd: boolean;
  getItemPermissions: (item: VendorLocationResponse) => {
    canEdit: boolean;
    canSetPrimary: boolean;
    canSetDefault: boolean;
    canToggleStatus: boolean;
    canDelete: boolean;
    isItemActive: boolean;
    isProtectedFromDeactivate: boolean;
  };
  onAdd: () => void;
  onEdit: (location: VendorLocationResponse) => void;
  onRowClick: (location: VendorLocationResponse) => void;
  onSetDefault: (location: VendorLocationResponse) => void;
  onToggleStatus: (location: VendorLocationResponse) => void;
  currentFontSizes: {
    title: number;
    subtitle: number;
    body: number;
    label: number;
    menuItem: number;
  };
}

const LocationsSection = ({
  locations,
  loading,
  includeInactive,
  onIncludeInactiveChange,
  canEdit: _canEdit,
  canAdd,
  getItemPermissions,
  onAdd,
  onEdit,
  onRowClick,
  onSetDefault,
  onToggleStatus,
  currentFontSizes: _currentFontSizes,
}: LocationsSectionProps) => {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();

  // Sorting state for Location table
  const [locationSortBy, setLocationSortBy] = useState<VendorLocationSortField | undefined>(undefined);
  const [locationSortOrder, setLocationSortOrder] = useState<0 | 1 | undefined>(undefined);

  return (
    <div className="tw-mt-8">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('vendor.sections.locations')}
        </h3>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => onIncludeInactiveChange(e.target.checked)}
          >
            {t('vendor.labels.includeInactiveLocations')}
          </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canAdd}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
            onClick={onAdd}
          >
            {t('vendor.modals.add')}
          </Button>
        </div>
      </div>
      <TableContainer>
        <Table<VendorLocationResponse>
          dataSource={
            locations
              ?.filter((l) => includeInactive || l.isActive)
              ?.slice()
              ?.sort((a, b) => {
                // Only apply default sort when no API sorting is active
                if (locationSortBy === undefined) {
                  // Default receiving first
                  if (a.isDefaultReceiving !== b.isDefaultReceiving) {
                    return a.isDefaultReceiving ? -1 : 1;
                  }
                  // Then by locationId (newer items have higher IDs)
                  return b.locationId - a.locationId;
                }
                // When API sorting is active, preserve the order from API
                return 0;
              }) || []
          }
          rowKey="locationId"
          rowClassName={(record) => !record.isActive ? 'inactive-row' : ''}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          onChange={(_pagination, _filters, sorter) => {
            // Handle sorting - map column key to API sortBy field
            const sortInfo = Array.isArray(sorter) ? sorter[0] : sorter;
            if (sortInfo?.columnKey && sortInfo?.order) {
              const columnToSortBy: Record<string, VendorLocationSortField> = {
                locationName: 1,
                isActive: 6,
                address: 7,
              };
              const sortBy = columnToSortBy[sortInfo.columnKey as string];
              if (sortBy !== undefined) {
                setLocationSortBy(sortBy);
                setLocationSortOrder(sortInfo.order === 'ascend' ? 0 : 1);
              }
            } else {
              // Clear sorting when user clears the sort
              setLocationSortBy(undefined);
              setLocationSortOrder(undefined);
            }
          }}
          columns={[
            {
              title: t('vendor.fields.defaultReceiving'),
              dataIndex: 'isDefaultReceiving',
              key: 'isDefaultReceiving',
              width: 160,
              align: 'center',
              render: (isDefaultReceiving: boolean) =>
                isDefaultReceiving ? (
                  <CheckOutlined
                    style={{ color: token.colorPrimary, fontSize: token.sizeMD }}
                  />
                ) : null,
            },
            {
              title: t('vendor.fields.locationName'),
              dataIndex: 'locationName',
              key: 'locationName',
              width: 180,
              sorter: true,
              sortOrder: locationSortBy === 1 ? (locationSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
            },
            {
              title: t('vendor.fields.address'),
              key: 'address',
              width: 300,
              ellipsis: true,
              sorter: true,
              sortOrder: locationSortBy === 7 ? (locationSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (_: unknown, record: VendorLocationResponse) => (
                <LocationAddressDisplay location={record} />
              ),
            },
            {
              title: t('vendor.fields.notes'),
              dataIndex: 'notes',
              key: 'notes',
              width: 200,
              render: (notes: string | null) => notes || '-',
            },
            {
              title: t('vendor.fields.actions'),
              key: 'actions',
              width: 80,
              fixed: 'right',
              onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
              render: (_: unknown, record: VendorLocationResponse) => {
                const itemPerms = getItemPermissions(record);
                return (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'setDefault',
                          label: t('vendor.actions.setAsDefault'),
                          icon: <CheckOutlined />,
                          disabled: !itemPerms.canSetDefault,
                          onClick: () => onSetDefault(record),
                        },
                        {
                          key: 'edit',
                          label: t('vendor.actions.edit'),
                          icon: <EditOutlined />,
                          disabled: !itemPerms.canEdit,
                          onClick: () => onEdit(record),
                        },
                        {
                          type: 'divider',
                        },
                        {
                          key: 'toggleStatus',
                          label: itemPerms.isItemActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate'),
                          icon: itemPerms.isItemActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                          ...getToggleStatusStyle(itemPerms, token),
                          onClick: () => onToggleStatus(record),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button type="text" icon={<MoreOutlined />} />
                  </Dropdown>
                );
              },
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default LocationsSection;
